import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useOAuthProviders } from '../../api/queries';
import { buttonClass } from '../../components/ui/button-class';
import { cn } from '../../lib/cn';

const PROVIDERS = [
  {
    id: 'google',
    label: 'Google',
    icon: (
      <svg viewBox="0 0 24 24" className="size-4" aria-hidden="true">
        <path
          fill="#4285F4"
          d="M22.5 12.3c0-.8-.1-1.5-.2-2.2H12v4.2h5.9a5 5 0 0 1-2.2 3.3v2.7h3.6c2-1.9 3.2-4.7 3.2-8Z"
        />
        <path
          fill="#34A853"
          d="M12 23c3 0 5.5-1 7.3-2.7l-3.6-2.7c-1 .7-2.2 1.1-3.7 1.1-2.9 0-5.3-1.9-6.2-4.5H2.1v2.8A11 11 0 0 0 12 23Z"
        />
        <path fill="#FBBC05" d="M5.8 14.2a6.6 6.6 0 0 1 0-4.3V7H2.1a11 11 0 0 0 0 9.9l3.7-2.7Z" />
        <path
          fill="#EA4335"
          d="M12 5.4c1.6 0 3.1.6 4.2 1.7l3.2-3.2A11 11 0 0 0 2.1 7l3.7 2.9C6.7 7.3 9.1 5.4 12 5.4Z"
        />
      </svg>
    ),
  },
  {
    id: 'github',
    label: 'GitHub',
    icon: (
      <svg viewBox="0 0 24 24" className="size-4" fill="currentColor" aria-hidden="true">
        <path d="M12 1.5a10.5 10.5 0 0 0-3.3 20.5c.5.1.7-.2.7-.5v-1.9c-2.9.6-3.5-1.3-3.5-1.3-.5-1.2-1.2-1.5-1.2-1.5-1-.6 0-.6 0-.6 1 .1 1.6 1.1 1.6 1.1.9 1.6 2.5 1.2 3.1.9.1-.7.4-1.2.7-1.4-2.3-.3-4.8-1.2-4.8-5.2 0-1.1.4-2.1 1.1-2.8-.1-.3-.5-1.4.1-2.8 0 0 .9-.3 2.9 1.1a10 10 0 0 1 5.2 0c2-1.4 2.9-1.1 2.9-1.1.6 1.4.2 2.5.1 2.8.7.7 1.1 1.7 1.1 2.8 0 4-2.5 4.9-4.8 5.2.4.3.7 1 .7 2v2.9c0 .3.2.6.7.5A10.5 10.5 0 0 0 12 1.5Z" />
      </svg>
    ),
  },
] as const;

/** Google and GitHub sign-in (FR-ACC-01). Providers the server has not configured are disabled. */
export function OAuthButtons() {
  const { t } = useTranslation();
  const providers = useOAuthProviders();
  const enabled = new Set(providers.data?.providers ?? []);
  const hintId = useId();
  const anyDisabled = providers.isSuccess && PROVIDERS.some((p) => !enabled.has(p.id));
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        {PROVIDERS.map((p) =>
          enabled.has(p.id) ? (
            <a
              key={p.id}
              href={`/api/v1/auth/oauth/${p.id}/start`}
              className={cn(buttonClass('secondary'), 'w-full')}
            >
              {p.icon}
              {t('auth.continueWith', { provider: p.label })}
            </a>
          ) : (
            <button
              key={p.id}
              type="button"
              disabled
              aria-describedby={hintId}
              className={cn(buttonClass('secondary'), 'w-full')}
            >
              {p.icon}
              {p.label}
            </button>
          ),
        )}
      </div>
      {anyDisabled && (
        <p id={hintId} className="text-center text-xs text-slate-500 dark:text-slate-400">
          {t('auth.oauthNotConfigured')}
        </p>
      )}
    </div>
  );
}

export function OrDivider() {
  const { t } = useTranslation();
  return (
    <div className="my-4 flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
      <span className="h-px flex-1 bg-slate-200 dark:bg-slate-800" />
      {t('auth.or')}
      <span className="h-px flex-1 bg-slate-200 dark:bg-slate-800" />
    </div>
  );
}
